// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Derives a customer's per-currency value metrics from their projects. Lifetime
 * value and active ARR each come back as one entry per currency, reusing the
 * project response's own contract value and annual recurring revenue rather
 * than a dedicated aggregate query.
 */
import { Currency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { getProjects } from '@/project/api/get-projects';
import { Decimal } from 'decimal.js';

interface CustomerValue {
  lifetimeValue: Currency[];
  activeARR: Currency[];
}

// Lists every currency in the shared order, zero-filling the ones this metric
// lacks, so the lifetime and ARR columns stay the same length and a given row
// is the same currency in both.
function listInOrder(
  totals: Map<IsoCurrencyCode, Decimal>,
  orderedCodes: IsoCurrencyCode[]
): Currency[] {
  return orderedCodes.map((currencyCode) => ({
    isoCode: currencyCode,
    amount: totals.get(currencyCode) ?? new Decimal(0),
  }));
}

/**
 * Retrieves a customer's lifetime value and active ARR, each split by currency.
 * Each active project reports ARR already grouped by currency, so every entry
 * is added to its own bucket; a project with no recurring revenue reports an
 * empty list and contributes nothing.
 *
 * @param customerId - The id of the customer to retrieve value metrics for
 * @returns Promise resolving to the per-currency value metrics
 * @throws Will throw if the underlying projects request fails
 */
export async function getCustomerValue(
  customerId: string
): Promise<CustomerValue> {
  const projectsResponse = await getProjects({ customerIds: [customerId] });

  const lifetimeTotals = new Map<IsoCurrencyCode, Decimal>();
  const arrTotals = new Map<IsoCurrencyCode, Decimal>();

  for (const project of projectsResponse.projects) {
    if (project.totalContractValue) {
      const { isoCode, amount } = project.totalContractValue;
      const running = lifetimeTotals.get(isoCode) ?? new Decimal(0);
      lifetimeTotals.set(isoCode, running.plus(amount));
    }

    if (project.status === 'ACTIVE') {
      for (const arr of project.annualRecurringRevenue) {
        const running = arrTotals.get(arr.isoCode) ?? new Decimal(0);
        arrTotals.set(arr.isoCode, running.plus(arr.amount));
      }
    }
  }

  // Order every currency by lifetime value descending so both lists share one
  // ordering; currencies with no lifetime value (ARR-only) fall to the end,
  // broken by ARR then ISO code. The ISO-code tiebreak makes the order fully
  // deterministic; a code-point compare keeps it locale-independent.
  const zero = new Decimal(0);
  const orderedCodes = Array.from(
    new Set<IsoCurrencyCode>([...lifetimeTotals.keys(), ...arrTotals.keys()])
  ).sort((left, right) => {
    const byLifetime = (lifetimeTotals.get(right) ?? zero).comparedTo(
      lifetimeTotals.get(left) ?? zero
    );
    if (byLifetime !== 0) {
      return byLifetime;
    }
    const byArr = (arrTotals.get(right) ?? zero).comparedTo(
      arrTotals.get(left) ?? zero
    );
    if (byArr !== 0) {
      return byArr;
    }
    if (left < right) {
      return -1;
    }
    if (left > right) {
      return 1;
    }
    return 0;
  });

  return {
    lifetimeValue: listInOrder(lifetimeTotals, orderedCodes),
    activeARR: listInOrder(arrTotals, orderedCodes),
  };
}
